import type { SecurityPerformanceIntradayPrice, SecurityPerformancePrice, SecurityTransaction } from '@/types';

export const PERFORMANCE_CACHE_VERSION = 3;
export const PERFORMANCE_PERIODS = ['1D', '5D', '1M', '3M', '6M', 'YTD', '1Y', '3Y', '5Y'] as const;
export type PerformancePeriod = (typeof PERFORMANCE_PERIODS)[number];
export const TABLE_PERFORMANCE_PERIODS = PERFORMANCE_PERIODS.filter((period) => period !== 'YTD');

export interface PerformancePoint {
  date: string;
  value: number;
}

function parseDate(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function subtractUtc(date: string, amount: number, unit: 'day' | 'month' | 'year'): string {
  const result = parseDate(date);
  if (unit === 'day') result.setUTCDate(result.getUTCDate() - amount);
  if (unit === 'month') result.setUTCMonth(result.getUTCMonth() - amount);
  if (unit === 'year') result.setUTCFullYear(result.getUTCFullYear() - amount);
  return formatDate(result);
}

export function getPortfolioInception(transactions: SecurityTransaction[]): string | null {
  if (transactions.length === 0) return null;
  return transactions.reduce(
    (earliest, transaction) => (transaction.date < earliest ? transaction.date : earliest),
    transactions[0]!.date
  );
}

export function getPositivePositionIds(transactions: SecurityTransaction[]): Set<string> {
  const units = new Map<string, number>();
  for (const transaction of transactions) {
    const delta = transaction.type === 'Buy' ? transaction.units : -transaction.units;
    units.set(transaction.securityId, (units.get(transaction.securityId) ?? 0) + delta);
  }
  return new Set(
    Array.from(units)
      .filter(([, value]) => value > 0)
      .map(([securityId]) => securityId)
  );
}

export function getPeriodBoundary(period: Exclude<PerformancePeriod, '1D'>, today: string): string {
  switch (period) {
    case '5D':
      return subtractUtc(today, 5, 'day');
    case '1M':
      return subtractUtc(today, 1, 'month');
    case '3M':
      return subtractUtc(today, 3, 'month');
    case '6M':
      return subtractUtc(today, 6, 'month');
    case 'YTD':
      return `${today.slice(0, 4)}-01-01`;
    case '1Y':
      return subtractUtc(today, 1, 'year');
    case '3Y':
      return subtractUtc(today, 3, 'year');
    case '5Y':
      return subtractUtc(today, 5, 'year');
  }
}

function sortedPrices(prices: SecurityPerformancePrice[], endDate: string): SecurityPerformancePrice[] {
  return prices.filter((price) => price.date <= endDate && price.price > 0).sort((a, b) => a.date.localeCompare(b.date));
}

function normalizedFromBoundary(
  prices: SecurityPerformancePrice[],
  boundary: string,
  endDate: string,
  inceptionDate: string | null
): PerformancePoint[] {
  if (inceptionDate && inceptionDate > boundary) return [];
  const ordered = sortedPrices(prices, endDate);
  const baseline = [...ordered].reverse().find((price) => price.date <= boundary);
  if (!baseline) return [];
  const following = ordered.filter((price) => price.date > boundary);
  if (following.length === 0) return [];
  return [
    { date: boundary, value: 0 },
    ...following.map((price) => ({ date: price.date, value: price.price / baseline.price - 1 })),
  ];
}

function createIntradaySeries(
  dailyPrices: SecurityPerformancePrice[],
  intradayPrices: SecurityPerformanceIntradayPrice[],
  period: '1D' | '5D'
): PerformancePoint[] {
  const ordered = [...intradayPrices].filter((price) => price.price > 0).sort((a, b) => a.timestamp - b.timestamp);
  if (ordered.length < 2) return [];
  const sessionDates = Array.from(
    new Set(ordered.map((price) => new Date(price.timestamp * 1000).toISOString().slice(0, 10)))
  );
  const selectedDates = period === '1D' ? sessionDates.slice(-1) : sessionDates.slice(-5);
  const selected = ordered.filter((price) =>
    selectedDates.includes(new Date(price.timestamp * 1000).toISOString().slice(0, 10))
  );
  if (selected.length < 2) return [];
  const firstSessionDate = selectedDates[0]!;
  const baseline = [...dailyPrices]
    .filter((price) => price.date < firstSessionDate && price.price > 0)
    .sort((a, b) => b.date.localeCompare(a.date))[0]?.price;
  const baselinePrice = baseline ?? selected[0]!.price;
  const baselineTimestamp = selected[0]!.timestamp - 1;
  return [
    { date: new Date(baselineTimestamp * 1000).toISOString(), value: 0 },
    ...selected.map((price) => ({
      date: new Date(price.timestamp * 1000).toISOString(),
      value: price.price / baselinePrice - 1,
    })),
  ];
}

export function createPerformanceSeries(
  prices: SecurityPerformancePrice[],
  period: PerformancePeriod,
  today: string,
  marketInception: string | null,
  intradayPrices: SecurityPerformanceIntradayPrice[] = []
): PerformancePoint[] {
  if ((period === '1D' || period === '5D') && intradayPrices.length > 0) {
    return createIntradaySeries(prices, intradayPrices, period);
  }
  if (period === '1D') {
    const ordered = sortedPrices(prices, today);
    if (ordered.length < 2) return [];
    const baseline = ordered[ordered.length - 2]!;
    const latest = ordered[ordered.length - 1]!;
    return [
      { date: baseline.date, value: 0 },
      { date: latest.date, value: latest.price / baseline.price - 1 },
    ];
  }
  return normalizedFromBoundary(prices, getPeriodBoundary(period, today), today, marketInception);
}

export function getPerformanceReturn(
  prices: SecurityPerformancePrice[],
  period: PerformancePeriod,
  today: string,
  marketInception: string | null,
  intradayPrices: SecurityPerformanceIntradayPrice[] = []
): number | null {
  const series = createPerformanceSeries(prices, period, today, marketInception, intradayPrices);
  return series.length < 2 ? null : series[series.length - 1]!.value;
}

export function getCalendarYearReturn(
  prices: SecurityPerformancePrice[],
  year: number,
  marketInception: string | null
): number | null {
  const boundary = `${year}-01-01`;
  const series = normalizedFromBoundary(prices, boundary, `${year}-12-31`, marketInception);
  return series.length < 2 ? null : series[series.length - 1]!.value;
}

export function getCompletedYears(portfolioInception: string, today: string): number[] {
  const firstYear = Number(portfolioInception.slice(0, 4));
  const currentYear = Number(today.slice(0, 4));
  const years: number[] = [];
  for (let year = currentYear - 1; year >= firstYear; year--) years.push(year);
  return years;
}

export function getRequiredHistoryStart(portfolioInception: string, today: string): string {
  const calendarStart = `${portfolioInception.slice(0, 4)}-01-01`;
  const fiveYearStart = subtractUtc(today, 5, 'year');
  return subtractUtc(calendarStart < fiveYearStart ? calendarStart : fiveYearStart, 10, 'day');
}

export function subtractDays(date: string, days: number): string {
  return subtractUtc(date, days, 'day');
}
